import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import apiClient from "../../pages/keycloak/axiosInstance";

const ViewProjectDetails = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [projectData, setProjectData] = useState(null);
  const [expandedSupplier, setExpandedSupplier] = useState(null);
  const baseURL = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    const fetchProjectData = async () => {
      try {
        const response = await apiClient.post(`get_project_detail`, { projectId });
        const data = {
          projectCreate: response.data.projectCreate || {},
          addSupplier: response.data.addSupplier?.map((supplier) => ({
            ...supplier,
            details: {
              ...supplier.details,
              moduleWattageData: supplier.details?.moduleWattageData || [],
              materials: supplier.details?.materials || [],
            },
          })) || [],
        };
        setProjectData(data);
      } catch (error) {
        console.error("Error fetching project data:", error);
      }
    };
    fetchProjectData();
  }, [projectId]);

  const toggleSupplier = (index) => {
    setExpandedSupplier(expandedSupplier === index ? null : index);
  };

  if (!projectData) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-lg font-medium text-gray-600">Loading project details...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="w-full mx-auto">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
          </svg>
          <span className="font-medium">Back</span>
        </button>

        {/* Project Header Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {projectData.projectCreate.projectName || "Untitled Project"}
          </h1>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
            <div>
              <p className="text-sm font-medium text-gray-500">Project ID</p>
              <p className="text-lg font-semibold text-gray-800">{projectData.projectCreate.projectId}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">SPV Name</p>
              <p className="text-lg font-semibold text-gray-800">{projectData.projectCreate.spvName || "—"}</p>
            </div>
          </div>
        </div>

        {/* Suppliers Section */}
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-gray-800">Suppliers ({projectData.addSupplier.length})</h2>

          {projectData.addSupplier.map((supplier, index) => (
            <div
              key={index}
              className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden transition-all hover:shadow-md"
            >
              {/* Supplier Header */}
              <button
                onClick={() => toggleSupplier(index)}
                className="w-full px-6 py-5 flex justify-between items-center bg-gradient-to-r from-gray-50 to-gray-100 hover:from-gray-100 hover:to-gray-200 transition-all"
              >
                <div>
                  <h3 className="text-xl font-bold text-gray-900">{supplier.name}</h3>
                  {supplier.shift && (
                    <p className="text-sm text-gray-600 mt-1">Shift: {supplier.shift}</p>
                  )}
                </div>
                <svg
                  className={`w-6 h-6 text-gray-600 transition-transform duration-200 ${
                    expandedSupplier === index ? "rotate-180" : ""
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {/* Expanded Content */}
              {expandedSupplier === index && (
                <div className="px-6 pb-8 pt-4 border-t border-gray-100">
                  {/* Module Details */}
                  {supplier.details && Object.keys(supplier.details).length > 0 && (
                    <>
                      <h4 className="text-lg font-semibold text-gray-800 mb-4">Module Specifications</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-8">
                        {[
                          { label: "Module Type", value: supplier.details.moduleType },
                          { label: "Cell Size", value: supplier.details.cellSize },
                          { label: "Cell Type", value: supplier.details.cellType },
                          { label: "Number of Cells", value: supplier.details.numberOfCells },
                          { label: "Module Size", value: supplier.details.moduleSize },
                          { label: "Mounting Holes", value: supplier.details.mountingHoles },
                          { label: "Technology", value: supplier.details.moduleTechnology },
                        ].map(({ label, value }) => (
                          value && (
                            <div key={label}>
                              <p className="text-sm font-medium text-gray-500">{label}</p>
                              <p className="text-base text-gray-900 font-medium">{value}</p>
                            </div>
                          )
                        ))}
                      </div>
                    </>
                  )}

                  {/* Module Wattage Data */}
                  {supplier.details?.moduleWattageData?.length > 0 && (
                    <>
                      <h4 className="text-lg font-semibold text-gray-800 mb-4">Module Wattage Breakdown</h4>
                      <div className="space-y-4">
                        {supplier.details.moduleWattageData.map((wattage, i) => (
                          <div key={i} className="bg-gray-50 rounded-xl p-5 border border-gray-200">
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                              <div>
                                <p className="text-sm text-gray-600">Wattage</p>
                                <p className="font-semibold">{wattage.moduleWattageWp} Wp</p>
                              </div>
                              <div>
                                <p className="text-sm text-gray-600">Quantity</p>
                                <p className="font-semibold">{wattage.moduleWattageNos}</p>
                              </div>
                              <div>
                                <p className="text-sm text-gray-600">Binning Option</p>
                                <p className="font-semibold">{wattage.binningOption || "—"}</p>
                              </div>
                              <div>
                                <p className="text-sm text-gray-600">Current Binning</p>
                                <p className="font-semibold text-xs">
                                  {wattage.i1CurrentBinning && `I1: ${wattage.i1CurrentBinning}`}<br/>
                                  {wattage.i2CurrentBinning && `I2: ${wattage.i2CurrentBinning}`}<br/>
                                  {wattage.i3CurrentBinning && `I3: ${wattage.i3CurrentBinning}`}
                                </p>
                              </div>
                            </div>
                            {(wattage.i1Location || wattage.i2Location || wattage.i3Location) && (
                              <div className="mt-3 pt-3 border-t border-gray-300">
                                <p className="text-sm text-gray-600 mb-2">Storage Locations</p>
                                <div className="text-sm space-y-1">
                                  {wattage.i1Location && <p>• I1: {wattage.i1Location}</p>}
                                  {wattage.i2Location && <p>• I2: {wattage.i2Location}</p>}
                                  {wattage.i3Location && <p>• I3: {wattage.i3Location}</p>}
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </>
                  )}

                  {/* Materials */}
                  {supplier.details?.materials?.length > 0 && (
                    <>
                      <h4 className="text-lg font-semibold text-gray-800 mt-8 mb-4">Materials Used</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        {supplier.details.materials.map((mat, i) => (
                          <div key={i} className="bg-gradient-to-br from-gray-50 to-gray-50 rounded-xl p-5 border border-gray-100">
                            <h5 className="font-bold text-gray-900">{mat.name || "Unnamed Material"}</h5>
                            <div className="mt-3 space-y-2 text-sm">
                              {mat.make && <p><span className="font-medium text-gray-600">Make:</span> {mat.make}</p>}
                              {mat.origin && <p><span className="font-medium text-gray-600">Origin:</span> {mat.origin}</p>}
                              {mat.description && (
                                <p className="mt-3 text-gray-700 italic">"{mat.description}"</p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ViewProjectDetails;